import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { activeAppLocale, AppLocale, DEFAULT_APP_LOCALE } from '../i18n/app-locale';

/**
 * Display text for the values the backend sends as keys (CLP-1364).
 *
 * Separate from `messages.xlf` on purpose. The XLF holds copy the frontend
 * authors and compiles into the bundle; this holds a catalogue the backend
 * owns — ~500 labels and option values across 28 products, delivered as
 * `labelKey` / `optionKeys` on
 * `GET /api/Products/types/conditions-preferences`. Keeping it an asset rather
 * than compiled code means the wording can change without an app release.
 *
 * Only display text is translated. The option *values* stay German, because
 * they are what gets submitted and what the Salesforce picklists match on.
 */
@Injectable({ providedIn: 'root' })
export class BackendTranslationsService {
  private readonly http = inject(HttpClient);
  private readonly locale: AppLocale = activeAppLocale(inject(LOCALE_ID));

  private entries: Readonly<Record<string, string>> = {};
  /** The German catalogue, kept for {@link sourceKeyFor}. */
  private sourceEntries: Readonly<Record<string, string>> = {};

  /**
   * Loads the catalogue for this build's locale. Called once at startup.
   *
   * The German catalogue is loaded alongside it in a non-German build, because
   * {@link sourceKeyFor} matches against German text. That is a second small
   * asset fetch; both are static JSON served from the same origin.
   *
   * A failure is not fatal: every caller passes the German text the API sends
   * alongside the key, so an empty catalogue degrades to German rather than
   * to blank labels or raw keys.
   */
  async load(): Promise<void> {
    this.entries = await this.fetchCatalogue(this.locale);
    this.sourceEntries =
      this.locale === DEFAULT_APP_LOCALE
        ? this.entries
        : await this.fetchCatalogue(DEFAULT_APP_LOCALE);
  }

  private async fetchCatalogue(
    locale: AppLocale,
  ): Promise<Readonly<Record<string, string>>> {
    try {
      return await firstValueFrom(
        this.http.get<Record<string, string>>(
          `assets/i18n/backend/${locale}.json`,
        ),
      );
    } catch (error) {
      console.error(
        `[i18n] backend catalogue "${locale}" could not be loaded`,
        error,
      );
      return {};
    }
  }

  /**
   * The key under `prefix` whose **German** text equals `text`.
   *
   * This is the reverse lookup the Flutter app uses (`appointment_text.dart`):
   * given a record the backend has not tagged with a key, recover the key by
   * matching the German it did send. Only for records with no `labelKey` and
   * no `code` — a key the API gave us is always more trustworthy than a
   * string comparison, which silently mislabels any record whose German text
   * happens to match another entry.
   */
  sourceKeyFor(prefix: string, text: string | null | undefined): string | undefined {
    const needle = text?.trim();
    if (!needle) return undefined;
    for (const [key, value] of Object.entries(this.sourceEntries)) {
      if (key.startsWith(prefix) && value.trim() === needle) return key;
    }
    return undefined;
  }

  /**
   * The key's text, or `fallback` when the key is absent.
   *
   * `params` fills the `@name` placeholders the catalogue uses (`@month`,
   * `@count`, `@date`, …). A placeholder with no matching param is left as
   * written rather than blanked, so a missing value is visible in review
   * instead of silently disappearing. Without `params` the text is returned
   * untouched — `@` is left alone so literal `@` in copy survives.
   */
  translate(
    key: string | null | undefined,
    fallback: string,
    params?: Readonly<Record<string, string | number>>,
  ): string {
    const text = (key ? this.entries[key] : undefined) ?? fallback;
    if (!params) return text;
    return text.replace(/@(\w+)/g, (match, name: string) =>
      params[name] === undefined ? match : String(params[name]),
    );
  }
}
